import React, { useEffect, useMemo, useState } from 'react';
import { FiKey, FiEye, FiEyeOff, FiChevronDown, FiChevronUp, FiCheckCircle } from 'react-icons/fi';
import { format } from 'date-fns';
import { aiTokenService } from '../../../services/aiTokenService';
import { AI_TEXT_PROVIDERS } from '../../../config/aiProviders';

const STATUS_BADGES = {
  valid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Connected' },
  invalid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Invalid credentials' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Not tested yet' },
};

function ProviderRow({ config, connection, isOpen, onToggle, onChanged, alert, confirm }) {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(connection?.model || config.defaultModel);
  const [showKey, setShowKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setModel(connection?.model || config.defaultModel);
  }, [connection?.model, config.defaultModel]);

  const badge = connection ? (STATUS_BADGES[connection.status] || STATUS_BADGES.pending) : null;
  const isActive = !!connection?.is_active;

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setMessage({ type: 'error', text: 'Please enter an API key.' });
      return;
    }
    setIsSaving(true);
    setMessage(null);
    const { error } = await aiTokenService.saveProvider({ provider: config.value, capability: 'text', apiKey, model });
    setIsSaving(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setApiKey('');
    setMessage({ type: 'success', text: `${config.label} connected successfully.` });
    await onChanged();
  };

  const handleTest = async () => {
    setIsTesting(true);
    setMessage(null);
    const { data, error } = await aiTokenService.testProvider('text', config.value);
    setIsTesting(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setMessage({ type: data.status === 'valid' ? 'success' : 'error', text: data.message });
    await onChanged();
  };

  const handleActivate = async () => {
    setIsActivating(true);
    setMessage(null);
    const { error } = await aiTokenService.activateProvider({ provider: config.value, capability: 'text' });
    setIsActivating(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setMessage({ type: 'success', text: `${config.label} is now the active AI provider.` });
    await onChanged();
  };

  const handleDisconnect = async () => {
    const ok = await confirm(`Disconnect ${config.label}? Content generation will stop using it${isActive ? ' — another connected provider will take over automatically if you have one' : ''}.`);
    if (!ok) return;
    setIsDisconnecting(true);
    const { error } = await aiTokenService.disconnectProvider('text', config.value);
    setIsDisconnecting(false);
    if (error) {
      alert(error.message);
      return;
    }
    setMessage(null);
    await onChanged();
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${isActive ? 'border-purple-300' : 'border-gray-200'}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-purple-100' : 'bg-gray-100'}`}>
            <FiKey className={`w-4 h-4 ${isActive ? 'text-purple-600' : 'text-gray-500'}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{config.label}</span>
              {isActive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                  <FiCheckCircle className="w-3 h-3" /> Active
                </span>
              )}
              {badge && (
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{connection ? `Model: ${connection.model || config.defaultModel}` : config.description}</p>
          </div>
        </div>
        {isOpen ? <FiChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <FiChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {isOpen && (
        <div className="px-5 pb-5 pt-1 bg-white border-t border-gray-100">
          {connection && (
            <div className="mb-4 mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <code className="text-xs text-gray-700">••••••••{connection.last_four}</code>
              <p className="text-xs text-gray-400 mt-1">
                Last tested: {connection.last_tested_at ? format(new Date(connection.last_tested_at), 'yyyy-MM-dd HH:mm') : 'Never'}
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button
                  onClick={handleTest}
                  disabled={isTesting}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-xs font-medium disabled:opacity-50"
                >
                  {isTesting ? 'Testing…' : 'Test Connection'}
                </button>
                {!isActive && (
                  <button
                    onClick={handleActivate}
                    disabled={isActivating}
                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs font-medium disabled:opacity-50"
                  >
                    {isActivating ? 'Activating…' : 'Activate'}
                  </button>
                )}
                <button
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs font-medium disabled:opacity-50"
                >
                  {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-3 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Model</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white text-sm"
              >
                {config.models.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">API Key</label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={connection ? 'Enter a new API key to replace the saved one' : `Enter your ${config.label} API key`}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showKey ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">{config.helperNote}</p>
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm hover:shadow disabled:opacity-50 text-sm"
            >
              {isSaving ? 'Saving…' : connection ? 'Update Key' : 'Save & Connect'}
            </button>
          </div>

          {message && (
            <p className={`text-xs mt-3 text-center ${message.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
              {message.text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AIProviderSection({ alert, confirm }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProvider, setOpenProvider] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await aiTokenService.getConnections('text');
    if (error) setLoadError(error.message);
    setConnections(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const byProvider = useMemo(() => {
    const map = {};
    for (const row of connections) map[row.provider] = row;
    return map;
  }, [connections]);

  // Default to expanding whichever provider is active (or the first one, if
  // nothing is connected yet) — only on the initial load, so the user's own
  // expand/collapse choices aren't overridden by later refreshes.
  useEffect(() => {
    if (loading || openProvider !== null) return;
    const active = connections.find((c) => c.is_active);
    setOpenProvider(active?.provider || AI_TEXT_PROVIDERS[0].value);
  }, [loading, connections, openProvider]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">AI Provider</h2>
        <p className="text-sm text-gray-500">
          Connect one or more AI providers and pick which one is active. The active provider powers article generation,
          recipe cards, titles, keywords, and every other AI-generated content task in Gymplix.
        </p>
      </div>

      {loadError && <p className="text-sm text-red-600 mb-4">Couldn't load your saved connections: {loadError}</p>}

      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
      ) : (
        <div className="space-y-3">
          {AI_TEXT_PROVIDERS.map((config) => (
            <ProviderRow
              key={config.value}
              config={config}
              connection={byProvider[config.value] || null}
              isOpen={openProvider === config.value}
              onToggle={() => setOpenProvider((cur) => (cur === config.value ? null : config.value))}
              onChanged={load}
              alert={alert}
              confirm={confirm}
            />
          ))}
        </div>
      )}
    </div>
  );
}
